import type React from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useKeycloakAuth } from '../../contexts/keycloak-auth-context';

interface KeycloakProtectedRouteProps {
  children: ReactNode;
  roles?: string[]; // Optional: Require specific roles
  requireAllRoles?: boolean; // If true, user must have ALL specified roles
}

export const KeycloakProtectedRoute: React.FC<KeycloakProtectedRouteProps> = ({
  children,
  roles = [],
  requireAllRoles = false,
}) => {
  const { isAuthenticated, isLoading, isInitialized, hasRole } = useKeycloakAuth();
  const location = useLocation();

  // Show loading spinner while Keycloak is initializing or loading
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Initializing authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Check role requirements if specified
  if (roles.length > 0) {
    const userHasRequiredRoles = requireAllRoles
      ? roles.every((role) => hasRole(role)) // User must have ALL roles
      : roles.some((role) => hasRole(role)); // User must have at least ONE role

    if (!userHasRequiredRoles) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};
